import React, { useEffect, useMemo, useState } from 'react';
import { Send, Printer, CheckCircle, AlertCircle, AlertTriangle, User, Search, EyeOff, RotateCcw, Minus, Plus, Package } from 'lucide-react';
import Modal from '../common/Modal';
import { DEFAULT_LABEL_COPIES } from './labelPrinting';
import { NO_ANSWER_LINE_ERROR } from './reportErrors';

const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

// Searchable list of production employees as large tap targets
const EmployeeList = ({ employees, selectedEmployee, onChoose }) => {
  const [filter, setFilter] = useState('');
  const term = filter.trim().toLowerCase();
  const shown = term ? employees.filter(emp => emp.name.toLowerCase().includes(term)) : employees;

  return (
    <div className="employee-list">
      {employees.length > 8 && (
        <label className="employee-filter">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find your name"
            aria-label="Find your name"
          />
        </label>
      )}
      <div className="employee-options">
        {shown.map(emp => (
          <button
            key={emp.userId}
            type="button"
            className={`employee-option ${String(emp.userId) === String(selectedEmployee) ? 'current' : ''}`}
            onClick={() => onChoose(emp.userId)}
          >
            {emp.name}
          </button>
        ))}
        {shown.length === 0 && <p className="employee-none">No one matches "{filter.trim()}".</p>}
      </div>
    </div>
  );
};

// Step badge: number until the step is done, then a tick
const Step = ({ n, done }) => (
  <span className={`confirm-step ${done ? 'done' : ''}`} aria-hidden="true">
    {done ? <CheckCircle size={16} /> : n}
  </span>
);

// "Who is reporting?" Starts blank every time: on a shared tablet the last person's name
// is never offered, so whoever reports has to pick their own name from the list.
const EmployeePicker = ({ employees, selectedEmployee, confirmed, onChoose }) => {
  const chosen = confirmed ? employees.find(emp => String(emp.userId) === String(selectedEmployee)) : null;
  const [choosing, setChoosing] = useState(false);
  const open = employees.length > 0 && (!chosen || choosing);

  return (
    <section className={`confirm-employee-picker ${chosen && !choosing ? 'done' : ''}`} aria-label="Who is reporting">
      <div className="confirm-step-row">
        <Step n={1} done={Boolean(chosen) && !choosing} />
        {chosen && !choosing ? (
          <div className="employee-confirmed">
            <span>Reporting as <strong>{chosen.name}</strong></span>
            <button type="button" className="link-btn" onClick={() => setChoosing(true)}>Change</button>
          </div>
        ) : (
          <h3 className="confirm-section-title"><User size={16} aria-hidden="true" /> Who is reporting?</h3>
        )}
      </div>
      {employees.length === 0 && (
        <p className="employee-none">The employee list could not be loaded, so this report won't record who made it. Reload the page to try again.</p>
      )}
      {open && (
        <EmployeeList
          employees={employees}
          selectedEmployee={chosen ? selectedEmployee : null}
          onChoose={(id) => { onChoose(id); setChoosing(false); }}
        />
      )}
    </section>
  );
};

// Good / reject figures and flags for one work order line
const ReportFigures = ({ row }) => (
  <div className="confirm-line">
    <div className="confirm-wo">
      <span className="confirm-wo-serial">{row.serialName}</span>
      {row.hidden && <span className="production-pill info">Not on screen</span>}
      {row.overBy > 0 && (
        <span className={`production-pill ${row.needsConfirm ? 'danger' : 'warning'}`}>
          Over plan +{row.overBy}
        </span>
      )}
      {row.overBy > 0 && <span className="confirm-wo-note">{row.remaining} left on the work order</span>}
    </div>
    <div className="confirm-figures">
      <span className="confirm-figure">
        <span className="confirm-figure-key">Made</span>
        <span className="confirm-good">{row.good}</span>
      </span>
      {row.reject > 0 && (
        <span className="confirm-figure">
          <span className="confirm-figure-key">Reject</span>
          <span className="confirm-reject">{row.reject}</span>
          <span className="confirm-reason">{row.reasonCode ? row.reasonLabel || row.reasonCode : 'No reason'}</span>
        </span>
      )}
    </div>
  </div>
);

// One card per product: what's being reported, and - for frames / mounts - the shelf
// count box. Blind on purpose: Priority's figure isn't shown until after the count, or
// people type what they see. `recount` maps part -> first count for parts whose
// difference was too big to post without a second count; those inputs are cleared and flagged.
const ReportItem = ({ title, sku, rows, part, value, isRecount, onCount }) => (
  <li className={`confirm-item ${isRecount ? 'recount' : ''} ${rows.some(r => r.hidden) ? 'is-hidden' : ''}`}>
    <div className={`confirm-item-main ${rows.length === 1 ? 'single' : ''}`}>
      <div className="confirm-item-title">
        <span className="confirm-item-desc">{title}</span>
        {sku && <span className="confirm-item-sku">{sku}</span>}
      </div>
      {rows.map(row => <ReportFigures key={row.serialName} row={row} />)}
      {isRecount && (
        <span className="shelf-count-recount" role="alert">
          That's a lot different from what Priority expects. Please count again carefully.
        </span>
      )}
    </div>
    {part && (
      <label className="confirm-count">
        <span className="confirm-count-key">On shelf now</span>
        <input
          className="shelf-count-input"
          type="number"
          inputMode="numeric"
          min="0"
          step="1"
          value={value ?? ''}
          onChange={(e) => onCount(part.partName, e.target.value)}
          placeholder="Count"
          aria-label={`On shelf now: ${part.description} (${part.partName})`}
        />
      </label>
    )}
  </li>
);

const isWholeCount = (value) => value !== undefined && value !== '' && /^\d+$/.test(String(value).trim());

// Confirmation before submitting to Priority. `rows` lists EVERY work order that will be
// submitted, including selections hidden by a filter or search - those are sorted first,
// tinted and announced in a banner, so nothing is reported that the operator can't see.
// Rows over plan are flagged; any row above 1.2x what's left needs a second tick.
export const ConfirmReportModal = ({
  employees,
  selectedEmployee,
  onEmployeeChange,
  rows,
  shelfCountParts = [],
  recount = {},
  isSubmitting,
  onCancel,
  onConfirm
}) => {
  const [employeeConfirmed, setEmployeeConfirmed] = useState(false);
  const [overAcknowledged, setOverAcknowledged] = useState(false);
  const [shelfCounts, setShelfCounts] = useState({});

  // A recount starts from an empty box, so the first number isn't just resubmitted
  useEffect(() => {
    const parts = Object.keys(recount);
    if (parts.length === 0) return;
    setShelfCounts(prev => {
      const next = { ...prev };
      parts.forEach(part => { delete next[part]; });
      return next;
    });
  }, [recount]);

  const countsMissing = shelfCountParts.some(part => !isWholeCount(shelfCounts[part.partName]));

  const hiddenCount = rows.filter(r => r.hidden).length;
  const overRows = rows.filter(r => r.overBy > 0);
  const confirmRows = rows.filter(r => r.needsConfirm);
  const totalGood = rows.reduce((sum, r) => sum + r.good, 0);
  const totalReject = rows.reduce((sum, r) => sum + r.reject, 0);
  const hasRejects = totalReject > 0;

  // Frames / mounts that need a shelf count carry their work orders; anything else
  // (blocks, or a count part with no row on screen) gets a card of its own.
  const items = useMemo(() => {
    const used = new Set();
    const counted = shelfCountParts.map(part => {
      const own = rows.filter(r => (part.serialNames || []).includes(r.serialName));
      own.forEach(r => used.add(r.serialName));
      return { key: `part-${part.partName}`, title: part.description, sku: part.partName, rows: own, part };
    });
    const rest = rows.filter(r => !used.has(r.serialName))
      .map(r => ({ key: `wo-${r.serialName}`, title: r.description, sku: r.partName, rows: [r], part: null }));
    // Not-on-screen selections first, so nobody misses them
    return [...counted, ...rest].sort((x, y) =>
      Number(y.rows.some(r => r.hidden)) - Number(x.rows.some(r => r.hidden)));
  }, [rows, shelfCountParts]);

  const chooseEmployee = (id) => {
    onEmployeeChange(id);
    setEmployeeConfirmed(true);
  };

  // If the employee list couldn't be loaded there is nobody to choose; reporting still
  // goes ahead (without a name), as it did before the picker existed.
  const blocker = !employeeConfirmed && employees.length > 0
    ? 'Choose who is reporting'
    : confirmRows.length > 0 && !overAcknowledged
      ? 'Tick the over-plan check'
      : countsMissing
        ? 'Enter the shelf count for every item'
        : null;

  const confirm = () => onConfirm(shelfCountParts.map(part => ({
    partName: part.partName,
    counted: Number(shelfCounts[part.partName]),
    firstCount: recount[part.partName],
    serialNames: part.serialNames
  })));

  return (
    <Modal
      open
      onClose={onCancel}
      title="Confirm production report"
      titleIcon={<Send size={20} />}
      size="lg"
      dismissible={!isSubmitting}
      className="production-modal"
      footer={(
        <>
          <p className="confirm-total">
            <strong>{totalGood.toLocaleString()}</strong> made
            {hasRejects && <> · <strong className="confirm-reject">{totalReject.toLocaleString()}</strong> reject</>}
            {' '}· {plural(rows.length, 'work order')}
            {blocker && <span className="confirm-blocker" role="status">{blocker}</span>}
          </p>
          <button type="button" className="production-btn secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="production-btn primary"
            onClick={confirm}
            disabled={isSubmitting || Boolean(blocker)}
          >
            {isSubmitting ? 'Submitting...' : <><Send size={16} /> Submit to Priority</>}
          </button>
        </>
      )}
    >
      {hiddenCount > 0 && (
        <div className="production-note info hidden-selection-banner" role="note">
          <EyeOff size={18} aria-hidden="true" />
          <span>
            <strong>{hiddenCount} of these {hiddenCount === 1 ? 'is' : 'are'} not on screen</strong> - hidden by a
            filter or search, but {hiddenCount === 1 ? 'it' : 'they'} will be reported too.
          </span>
        </div>
      )}

      <EmployeePicker
        employees={employees}
        selectedEmployee={selectedEmployee}
        confirmed={employeeConfirmed}
        onChoose={chooseEmployee}
      />

      <section className="confirm-report" aria-label="Work orders to report">
        <div className="confirm-step-row">
          <Step n={2} done={shelfCountParts.length > 0 && !countsMissing} />
          <h3 className="confirm-section-title">
            {shelfCountParts.length > 0 ? (
              <><Package size={16} aria-hidden="true" /> Count the shelf</>
            ) : 'Check what you are reporting'}
          </h3>
        </div>
        {shelfCountParts.length > 0 && (
          <p className="shelf-count-help">
            Type how many are on the shelf <strong>now</strong>, not counting the ones you've just made.
          </p>
        )}
        <ul className="confirm-items">
          {items.map(item => (
            <ReportItem
              key={item.key}
              {...item}
              value={item.part ? shelfCounts[item.part.partName] : undefined}
              isRecount={item.part ? recount[item.part.partName] !== undefined : false}
              onCount={(partName, value) => setShelfCounts(prev => ({ ...prev, [partName]: value }))}
            />
          ))}
        </ul>
      </section>

      {overRows.length > 0 && (
        <div className="production-note warning" role="note">
          <AlertTriangle size={18} aria-hidden="true" />
          <div>
            <p>
              <strong>{plural(overRows.length, 'work order')} over plan</strong>, by{' '}
              {overRows.reduce((sum, r) => sum + r.overBy, 0).toLocaleString()} in total.
            </p>
            {confirmRows.length > 0 && (
              <label className="over-ack">
                <input
                  type="checkbox"
                  checked={overAcknowledged}
                  onChange={(e) => setOverAcknowledged(e.target.checked)}
                />
                <span>
                  I've checked {confirmRows.map(r => r.serialName).join(', ')} - report more than is left
                  on {confirmRows.length === 1 ? 'it' : 'them'}.
                </span>
              </label>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

const LABEL_LAYOUTS = [
  { id: '1', name: '1 per page', desc: 'Full page label', cells: 1 },
  { id: '4', name: '4 per page', desc: '99.1 x 139mm', cells: 4 },
  { id: '21', name: '21 per page', desc: '63.5 x 38.1mm', cells: 21 }
];

// Label options: layout (remembered between prints), copies per work order (defaults to
// the layout's usual number) and which work orders to print.
export const LabelPickerModal = ({ items, initialLayout, onPrint, onCancel }) => {
  const [layout, setLayout] = useState(LABEL_LAYOUTS.some(l => l.id === initialLayout) ? initialLayout : '4');
  const [copies, setCopies] = useState(DEFAULT_LABEL_COPIES[layout]);
  const [chosen, setChosen] = useState(() => new Set(items.map(item => item.serialName)));

  const chooseLayout = (id) => {
    setLayout(id);
    setCopies(DEFAULT_LABEL_COPIES[id]);
  };

  const toggle = (serialName) => setChosen(prev => {
    const next = new Set(prev);
    if (next.has(serialName)) next.delete(serialName); else next.add(serialName);
    return next;
  });

  const setCopiesFrom = (value) => {
    const n = parseInt(value, 10);
    if (!isNaN(n)) setCopies(Math.min(99, Math.max(1, n)));
  };

  const labelTotal = chosen.size * copies;

  return (
    <Modal
      open
      onClose={onCancel}
      title="Print labels"
      titleIcon={<Printer size={20} />}
      size="md"
      className="production-modal"
      footer={(
        <>
          <button type="button" className="production-btn secondary" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="production-btn primary"
            disabled={chosen.size === 0}
            onClick={() => onPrint({ layout, copies, serialNames: chosen })}
          >
            <Printer size={16} /> Print {plural(labelTotal, 'label')}
          </button>
        </>
      )}
    >
      <fieldset className="label-fieldset">
        <legend>Layout</legend>
        <div className="label-layout-options">
          {LABEL_LAYOUTS.map(option => (
            <button
              key={option.id}
              type="button"
              className={`label-layout-option ${layout === option.id ? 'active' : ''}`}
              aria-pressed={layout === option.id}
              onClick={() => chooseLayout(option.id)}
            >
              <div className={`layout-preview layout-${option.id}`} aria-hidden="true">
                {[...Array(option.cells)].map((_, i) => <div key={i} className="layout-label-preview"></div>)}
              </div>
              <span className="layout-name">{option.name}</span>
              <span className="layout-desc">{option.desc}</span>
            </button>
          ))}
        </div>
      </fieldset>

      <div className="label-copies">
        <label htmlFor="label-copies-input">Copies per work order</label>
        <div className="qty-stepper">
          <button
            type="button"
            className="qty-stepper-btn"
            onClick={() => setCopies(c => Math.max(1, c - 1))}
            disabled={copies <= 1}
            aria-label="One copy fewer"
          >
            <Minus size={18} />
          </button>
          <input
            id="label-copies-input"
            type="number"
            className="label-copies-input"
            min="1"
            max="99"
            inputMode="numeric"
            value={copies}
            onChange={(e) => setCopiesFrom(e.target.value)}
          />
          <button
            type="button"
            className="qty-stepper-btn"
            onClick={() => setCopies(c => Math.min(99, c + 1))}
            aria-label="One copy more"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>

      {items.length > 1 && (
        <fieldset className="label-fieldset">
          <legend>Work orders ({chosen.size} of {items.length})</legend>
          <div className="label-wo-list">
            {items.map(item => (
              <label key={item.serialName} className="label-wo-option">
                <input
                  type="checkbox"
                  checked={chosen.has(item.serialName)}
                  onChange={() => toggle(item.serialName)}
                />
                <span className="label-wo-serial">{item.serialName}</span>
                <span className="label-wo-part">{item.partDescription || item.partName || ''}</span>
                <span className="label-wo-qty">x {item.quantity}</span>
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </Modal>
  );
};

// Collapsed Priority text behind a plain reason
const TechnicalDetails = ({ detail }) => (
  detail ? (
    <details className="technical-details">
      <summary>Technical details</summary>
      <pre>{detail}</pre>
    </details>
  ) : null
);

const LineList = ({ lines, tone }) => (
  <ul className={`result-lines ${tone}`}>
    {lines.map(line => (
      <li key={line.serialName} className="result-line">
        <div className="result-line-head">
          <span className="result-line-serial">{line.serialName}</span>
          <span className="result-line-part">{line.description}</span>
          {tone === 'ok' && (
            <span className="result-line-qty">
              {line.good} good{line.reject > 0 && <>, <span className="confirm-reject">{line.reject} reject</span></>}
              {line.al && <span className="result-line-al">AL {line.al}</span>}
            </span>
          )}
        </div>
        {tone === 'failed' && (
          <>
            <p className="result-line-reason">{line.message}</p>
            <TechnicalDetails detail={line.detail} />
          </>
        )}
      </li>
    ))}
  </ul>
);

// Outcome of a submission.
//   success  every line reported
//   partial  report created, some lines refused (HTTP 207) - the failed lines stay selected
//   failed   nothing reported, or no answer from Priority
// Warnings (raw materials not issued, local copy not updated) stay in the dialog until
// it is closed instead of flashing past in a toast.
const signed = (n) => (n > 0 ? `+${n}` : String(n));

// What the shelf count found, now that it's safe to show Priority's figures, and what
// should be in stock now: the count plus the good quantity just reported.
// `reportedByPart` is null when the report's outcome is unknown (no answer from Priority).
export const ShelfCountResults = ({ shelfCount, reportedByPart }) => {
  if (!shelfCount) return null;
  if (shelfCount.error) {
    return (
      <div className="production-note warning" role="note">
        <AlertTriangle size={18} aria-hidden="true" />
        <span>Shelf count not checked - Priority's stock was not corrected. {shelfCount.error}</span>
      </div>
    );
  }
  const { results } = shelfCount;
  if (!results || results.length === 0) return null;
  return (
    <section className="result-section">
      <h3 className="result-section-title">Shelf count</h3>
      <table className="confirm-table shelf-count-results">
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col" className="num">Expected</th>
            <th scope="col" className="num">Counted</th>
            <th scope="col" className="num">Correction</th>
            {reportedByPart && <th scope="col" className="num">Now in stock</th>}
          </tr>
        </thead>
        <tbody>
          {results.map(r => (
            <tr key={r.partName}>
              <td>
                <div className="confirm-wo-serial">{r.partName}</div>
                <div className="confirm-wo-note">
                  Priority {r.priorityBalance}
                  {r.pickedNotShipped > 0 && <>, {r.pickedNotShipped} picked not shipped</>}
                  {r.openOrders !== null && <> · Open orders {r.openOrders}</>}
                  {r.projected !== null && <> · Projected {r.projected}</>}
                </div>
                {r.error && <div className="shelf-count-error">{r.error}</div>}
              </td>
              <td className="num">{r.expected}</td>
              <td className="num">{r.counted}</td>
              <td className="num">
                {r.status === 'corrected' && (
                  <span className={`production-pill ${r.difference < 0 ? 'danger' : 'warning'}`} title={r.docNo ? `Count document ${r.docNo}` : undefined}>
                    {signed(r.difference)}
                  </span>
                )}
                {r.status === 'matched' && <span className="production-pill success">Matches</span>}
                {(r.status === 'failed' || r.status === 'skipped') && (
                  <span className="production-pill danger">Not posted {signed(r.difference)}</span>
                )}
              </td>
              {reportedByPart && (
                <td className="num shelf-count-now" title={`${r.counted} counted + ${reportedByPart[r.partName] || 0} just reported`}>
                  {r.counted + (reportedByPart[r.partName] || 0)}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

export const ReportResultModal = ({ result, onPrintLabels, onRetryFailed, onClose }) => {
  const { kind, reportNo, reported, failed, warnings, message, isNetworkError, duplicate, shelfCount } = result;
  const reportedByPart = useMemo(() => (isNetworkError ? null : reported.reduce((acc, line) => {
    if (line.partName) acc[line.partName] = (acc[line.partName] || 0) + line.good;
    return acc;
  }, {})), [reported, isNetworkError]);
  const totals = useMemo(() => ({
    good: reported.reduce((sum, line) => sum + line.good, 0),
    reject: reported.reduce((sum, line) => sum + line.reject, 0)
  }), [reported]);

  const title = kind === 'success'
    ? 'Production reported'
    : kind === 'partial'
      ? `Partly reported${reportNo ? ` — report ${reportNo}` : ''}`
      : isNetworkError ? 'No answer from Priority' : 'Nothing was reported';
  const tone = kind === 'success' ? 'success' : kind === 'partial' ? 'warning' : 'danger';
  // A line Priority never answered may already be reported, and the retry gets a new batch
  // key - so don't offer a one-click retry that could report it twice.
  const unanswered = failed.some(line => line.message === NO_ANSWER_LINE_ERROR);
  const Icon = kind === 'success' ? CheckCircle : kind === 'partial' ? AlertTriangle : AlertCircle;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      titleIcon={<Icon size={22} />}
      tone={tone}
      size="lg"
      className="production-modal"
      footer={(
        <>
          {reported.length > 0 && (
            <button type="button" className="production-btn secondary" onClick={onPrintLabels}>
              <Printer size={16} /> Print labels
            </button>
          )}
          {kind === 'partial' && failed.length > 0 && !unanswered ? (
            <>
              <button type="button" className="production-btn secondary" onClick={onClose}>Close</button>
              <button type="button" className="production-btn primary" onClick={onRetryFailed}>
                <RotateCcw size={16} /> Retry failed lines
              </button>
            </>
          ) : (
            <button type="button" className="production-btn primary" onClick={onClose}>
              {kind === 'success' ? 'Done' : 'Close'}
            </button>
          )}
        </>
      )}
    >
      {kind === 'success' && (
        <p className="result-summary">
          {duplicate
            ? message
            : <>Sent to Priority: {plural(reported.length, 'work order')}, {totals.good.toLocaleString()} good{totals.reject > 0 && `, ${totals.reject.toLocaleString()} rejected`}.</>}
        </p>
      )}
      {kind === 'failed' && !isNetworkError && <p className="result-summary">{message}</p>}
      {kind === 'failed' && isNetworkError && (
        <>
          <p className="result-summary">
            The report may or may not have reached Priority. Submitting the same lines and quantities again is
            safe - it won't be reported twice.
          </p>
          <TechnicalDetails detail={message} />
        </>
      )}
      {kind === 'partial' && (
        <p className="result-summary">
          {plural(reported.length, 'line')} reported, {plural(failed.length, 'line')} refused. The refused lines are
          still selected so you can fix them and retry.
        </p>
      )}

      {reportNo && kind !== 'partial' && (
        <p className="result-report-no">Report number <strong>{reportNo}</strong></p>
      )}

      {warnings.map(warning => (
        <div key={warning} className="production-note warning" role="note">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{warning}</span>
        </div>
      ))}

      <ShelfCountResults shelfCount={shelfCount} reportedByPart={reportedByPart} />

      {reported.length > 0 && (
        <section className="result-section">
          <h3 className="result-section-title ok">Reported ({reported.length})</h3>
          <LineList lines={reported} tone="ok" />
        </section>
      )}

      {failed.length > 0 && (
        <section className="result-section">
          <h3 className="result-section-title failed">Not reported ({failed.length})</h3>
          <LineList lines={failed} tone="failed" />
        </section>
      )}
    </Modal>
  );
};
